import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["leftContent", "rightContent", "leftButton", "rightButton"]
  static values = {
    leftActive: String,
    rightActive: String
  }

  connect() {
    // Load saved preferences from localStorage
    this.loadPreferences()
  }

  selectLeft(event) {
    const contentType = event.currentTarget.dataset.contentType
    this.setLeftPane(contentType)
    this.savePreferences()
  }

  selectRight(event) {
    const contentType = event.currentTarget.dataset.contentType
    this.setRightPane(contentType)
    this.savePreferences()
  }

  setLeftPane(type) {
    // Update value
    this.leftActiveValue = type

    // Hide all left content sections
    this.leftContentTargets.forEach(content => {
      if (content.dataset.contentType === type) {
        content.classList.remove('hidden')
        // Trigger map resize if showing a map
        if (type === 'map') {
          this.resizeMapInContent(content)
        }
      } else {
        content.classList.add('hidden')
      }
    })

    // Update button active states
    this.leftButtonTargets.forEach(button => {
      if (button.dataset.contentType === type) {
        this.activateButton(button)
      } else {
        this.deactivateButton(button)
      }
    })
  }

  setRightPane(type) {
    // Update value
    this.rightActiveValue = type

    // Hide all right content sections
    this.rightContentTargets.forEach(content => {
      if (content.dataset.contentType === type) {
        content.classList.remove('hidden')
        // Trigger map resize if showing a map
        if (type === 'map') {
          this.resizeMapInContent(content)
        }
      } else {
        content.classList.add('hidden')
      }
    })

    // Update button active states
    this.rightButtonTargets.forEach(button => {
      if (button.dataset.contentType === type) {
        this.activateButton(button)
      } else {
        this.deactivateButton(button)
      }
    })
  }

  activateButton(button) {
    button.classList.remove('text-gray-600', 'hover:text-gray-900', 'hover:bg-gray-50')
    button.classList.add('bg-white', 'text-sapphire', 'font-medium', 'shadow-sm')
  }

  deactivateButton(button) {
    button.classList.remove('bg-white', 'text-sapphire', 'font-medium', 'shadow-sm')
    button.classList.add('text-gray-600', 'hover:text-gray-900', 'hover:bg-gray-50')
  }

  savePreferences() {
    localStorage.setItem('response_preview_left', this.leftActiveValue)
    // Annotation is an opt-in mode — never persist it as the default right pane.
    if (this.rightActiveValue !== 'annotate') {
      localStorage.setItem('response_preview_right', this.rightActiveValue)
    }
  }

  loadPreferences() {
    const left = localStorage.getItem('response_preview_left')
    const right = localStorage.getItem('response_preview_right')

    // Only apply saved preferences if the content is available
    if (left && this.isPaneAvailable('left', left)) {
      this.setLeftPane(left)
    }

    if (right && this.isPaneAvailable('right', right)) {
      this.setRightPane(right)
    }
  }

  isPaneAvailable(pane, type) {
    // Check if a content section exists for this type in this pane
    const targets = pane === 'left' ? this.leftContentTargets : this.rightContentTargets
    return targets.some(content => content.dataset.contentType === type)
  }

  resizeMapInContent(content) {
    // Find map controller in the content and trigger resize
    const mapElement = content.querySelector('[data-controller~="map"]')
    if (mapElement) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        const mapController = this.application.getControllerForElementAndIdentifier(mapElement, 'map')
        if (mapController && typeof mapController.resize === 'function') {
          mapController.resize()
        }
      })
    }
  }
}
